'use client';
import { upsellViewedEvent } from '#pplx/frontend/apps/ask/events/unified-upsell/analytics.js';
import { isCtaSupported } from '#pplx/frontend/apps/ask/hooks/upsells-hooks/handlers/executeCtaHandler.js';
import { useHomepageUpsellsQuery } from '#pplx/frontend/apps/ask/hooks/upsells-hooks/useHomepageUpsellsQuery.js';
import { usePageBottomUpsellAction } from '#pplx/frontend/apps/ask/upsell-slots/comet-ntp-bottom/internal/usePageBottomUpsellAction.js';
import { buildUpsellSlotAnalytics } from '#pplx/frontend/apps/ask/upsell-slots/UpsellSlotRenderPlan.js';
import { useIsSubscribed } from '#pplx/frontend/auth/useIsSubscribed.js';
import { useInstrumentation } from '#pplx/frontend/framework/instrumentation/useInstrumentation.js';
import { useEffect } from 'react';
const isPageBottomFooterUpsellRequestEnabled = (input) => input.callsite.enabled !== false &&
    input.callsite.surface?.isAllowedFloatingPath !== false &&
    input.callsite.surface?.isMobileLayout !== true &&
    input.callsite.surface?.canShowInViewport !== false &&
    input.callsite.surface?.hasPrimarySurfaceContent !== true;
export const resolvePageBottomFooterUpsellSlotPlan = ({ input, data, controls, }) => {
    const enabled = input.callsite.enabled !== false && input.callsite.surface?.isAllowedFloatingPath !== false;
    if (!enabled) {
        return { status: 'empty', show: false, reason: 'disabled' };
    }
    if (input.callsite.surface?.isMobileLayout ||
        input.callsite.surface?.canShowInViewport === false) {
        return { status: 'blocked', show: false, reason: 'blocked_by_callsite' };
    }
    if (input.callsite.surface?.hasPrimarySurfaceContent) {
        return { status: 'blocked', show: false, reason: 'competing_surface' };
    }
    if (data.isLoading) {
        return { status: 'loading', show: false, reason: 'fetch_loading', isLoading: true };
    }
    if (data.isError) {
        return { status: 'error', show: false, reason: 'fetch_error', isError: true };
    }
    const { upsellInformation } = data;
    if (!upsellInformation) {
        return { status: 'empty', show: false, reason: 'no_backend_upsell' };
    }
    if (!upsellInformation.app_location ||
        !input.acceptedLocations.includes(upsellInformation.app_location)) {
        return { status: 'blocked', show: false, reason: 'location_mismatch' };
    }
    if (upsellInformation.cta && !isCtaSupported(upsellInformation.cta)) {
        return { status: 'blocked', show: false, reason: 'unsupported_cta' };
    }
    if (!upsellInformation.title || !upsellInformation.button_text) {
        return { status: 'blocked', show: false, reason: 'missing_required_fields' };
    }
    return {
        status: 'ready',
        show: true,
        slotId: input.slotId,
        upsellInformation,
        render: {
            kind: 'page_bottom_footer',
            componentKey: 'PageBottomFooter',
            props: {
                isMax: data.isMax,
                onClick: controls.onClick,
                upsellInformation,
            },
        },
        actions: controls,
        analytics: buildUpsellSlotAnalytics(input.slotId, upsellInformation),
    };
};
export const usePageBottomFooterUpsellSlotPlan = ({ input, }) => {
    const enabled = isPageBottomFooterUpsellRequestEnabled(input);
    const { productBanner, isError, isLoading } = useHomepageUpsellsQuery({
        enabled,
        location: input.source.request.location,
    });
    const { isMax } = useIsSubscribed();
    const { impression } = useInstrumentation();
    const handleUpsell = usePageBottomUpsellAction({
        upsellInformation: productBanner || undefined,
    });
    const plan = resolvePageBottomFooterUpsellSlotPlan({
        input,
        data: {
            isError,
            isLoading,
            isMax,
            upsellInformation: productBanner,
        },
        controls: {
            onClick: handleUpsell,
        },
    });
    const readyUpsellInformation = plan.show ? plan.upsellInformation : null;
    useEffect(() => {
        if (!enabled || !readyUpsellInformation) {
            return;
        }
        impression(upsellViewedEvent({
            name: 'upsell viewed',
            data: {
                upsellLocation: readyUpsellInformation.app_location ?? input.slotId,
                upsellUUID: readyUpsellInformation.upsell_uuid ?? '',
                upsellName: readyUpsellInformation.name ?? '',
                upsellType: readyUpsellInformation.upsell_type ?? '',
                cta: readyUpsellInformation.cta,
            },
        }));
    }, [
        enabled,
        input.slotId,
        readyUpsellInformation,
        readyUpsellInformation?.app_location,
        readyUpsellInformation?.cta,
        readyUpsellInformation?.name,
        readyUpsellInformation?.upsell_type,
        readyUpsellInformation?.upsell_uuid,
        impression,
    ]);
    return plan;
};
//# sourceMappingURL=usePageBottomFooterUpsellSlotPlan.js.map